import { Injectable } from '@angular/core';
import { IMenuCategory, IStock } from '@app/state/merchant-state/interfaces';
import { Observable } from 'rxjs';
import { map, pluck } from 'rxjs/operators';
import { TableData } from '@shared/state-table/interfaces/table-data.interface';
import { EnvironmentService } from '@core/environment/services/environment.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { PaginatedResponse } from '@shared/state-table/interfaces/paginated-response.type';
import { authHeaders } from '@core/authentication/constants/auth-headers.constant';
import { Util } from '@app/utils/utils';

@Injectable({
  providedIn: 'root',
})
export class StockDataService {
  constructor(private httpClient: HttpClient, private environment: EnvironmentService) {}

  private getEndpoint(merchantId: string, prefix: string, suffix?: string): string {
    return `${this.environment.apiEndpoint}/merchants/${merchantId}/${prefix}${suffix ? `/${suffix}` : ''}`;
  }

  getMenuCategories(merchantId: string): Observable<IMenuCategory[]> {
    return this.httpClient.get<IMenuCategory[]>(this.getEndpoint(merchantId, 'menuCategories'));
  }

  getMenuCategory(merchantId: string, restaurantMenuCategoryId: string): Observable<IMenuCategory> {
    return this.httpClient.get<IMenuCategory>(this.getEndpoint(merchantId, 'menuCategories', restaurantMenuCategoryId));
  }

  createMenuCategory(merchantId: string, formData: FormData): Observable<IMenuCategory> {
    return this.httpClient.post<IMenuCategory>(this.getEndpoint(merchantId, 'menuCategories'), formData, {
      headers: { [authHeaders.generatedContentType]: 'true' },
    });
  }

  updateMenuCategory(merchantId: string, restaurantMenuCategoryId: string, formData: FormData): Observable<IMenuCategory> {
    return this.httpClient.patch<IMenuCategory>(this.getEndpoint(merchantId, 'menuCategories', restaurantMenuCategoryId), formData, {
      headers: { [authHeaders.generatedContentType]: 'true' },
    });
  }

  deleteMenuCategory(merchantId: string, restaurantMenuCategoryId: string): Observable<void> {
    return this.httpClient.delete<void>(this.getEndpoint(merchantId, 'menuCategories', restaurantMenuCategoryId));
  }

  reorderMenuCategories(merchantId: string, categoryIds: string[]): Observable<IMenuCategory[]> {
    return this.httpClient.put<IMenuCategory[]>(this.getEndpoint(merchantId, 'reorderMenuCategories'), categoryIds);
  }

  getStockItem(merchantId: string, merchantItemId: string): Observable<IStock> {
    return this.httpClient.get<IStock>(this.getEndpoint(merchantId, 'items', merchantItemId));
  }

  getStockItems(merchantId: string): Observable<IStock[]> {
    return this.httpClient.get<{ items: IStock[] }>(this.getEndpoint(merchantId, 'items')).pipe(pluck('items'));
  }

  createStockItem(merchantId: string, stock: IStock): Observable<IStock> {
    const formData = Util.createFormDataFromObject(stock, ['image', 'largeImageUrl', 'xLargeImageUrl']);
    return this.httpClient.post<IStock>(this.getEndpoint(merchantId, 'items'), formData, {
      headers: { [authHeaders.generatedContentType]: 'true' },
    });
  }

  updateStockItem(merchantId: string, merchantItemId: string, stock: IStock): Observable<IStock> {
    const formData = Util.createFormDataFromObject(stock, ['image', 'largeImageUrl', 'xLargeImageUrl']);
    return this.httpClient.patch<IStock>(this.getEndpoint(merchantId, 'items', merchantItemId), formData, {
      headers: { [authHeaders.generatedContentType]: 'true' },
    });
  }

  deleteStockItem(merchantId: string, merchantItemId: string): Observable<void> {
    return this.httpClient.delete<void>(this.getEndpoint(merchantId, 'items', merchantItemId));
  }

  deleteStockItems(merchantId: string, itemIds: string[], comboIds: string[]): Observable<void> {
    return this.httpClient.put<void>(this.getEndpoint(merchantId, 'deleteItems'), {
      items: itemIds,
      combos: comboIds,
    });
  }

  reorderStockItems(merchantId: string, categoryId: string, merchantItemIds: string[]): Observable<IStock[]> {
    const url = `${this.environment.apiEndpoint}/merchants/${merchantId}/menuCategories/${categoryId}/reorderItems`;
    return this.httpClient.put<IStock[]>(url, merchantItemIds);
  }

  importStockItems(merchantId: string, formData: FormData): Observable<void> {
    const url = `${this.environment.apiEndpoint}/merchants/${merchantId}/import/items`;
    return this.httpClient.post<void>(url, formData, { headers: { [authHeaders.generatedContentType]: 'true' } });
  }

  exportStockItems(merchantId: string, params: HttpParams): Observable<Blob> {
    const url = `${this.environment.apiEndpoint}/merchants/${merchantId}/items-export`;
    return this.httpClient.get(url, { params, responseType: 'blob' });
  }

  getPaginatedStockItems(merchantId: string, params: HttpParams): Observable<TableData<IStock>> {
    const url = this.getEndpoint(merchantId, 'items');
    return this.httpClient.get<PaginatedResponse<IStock, 'items'>>(url, { params }).pipe(
      map(({ items, paging }) => ({
        items,
        total: paging.count,
      }))
    );
  }
}
